import { useEffect, useMemo, useState } from "react"
import { Card, CardContent } from "./ui/card"
import { HeartPulse, Footprints, Activity, Flame, Route, Building, Clock } from "lucide-react"
import { DataService } from "../services/data.service"
import { WatchHrSample, WatchDay, WatchActivityState } from "../models/watchHealth.model"
import { DateTime } from "luxon"
import { CartesianGrid, Line, LineChart, Bar, BarChart, XAxis, YAxis } from "recharts"
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "./ui/chart"

export interface WatchHealthReportProps {
    beneficiaryUserId: number | null   // user.person_user_id
    timezone: string                   // user.time_zone
    // Bumped by the page's 15-min silent refresh to re-pull the watch feeds; the
    // beneficiaryUserId alone is stable, so without this the panel never refreshes
    // on the page cadence (e.g. an on_demand HR sample landing). No flicker —
    // state isn't cleared on refetch.
    refreshKey?: number
}

// History window. Default 30; the toggle lets a caregiver widen/narrow it.
type Range = 7 | 30 | 90

// Fallback window used when the selected range comes back empty. A watch that
// stopped reporting months ago would otherwise hide the whole card (and with it
// the range toggle), so there'd be no way to reach the data that does exist.
// The heart-rate feed buckets long windows server-side, so this stays cheap.
const ALL_HISTORY_DAYS = 3650

const hrConfig: ChartConfig = { bpm: { label: "Heart rate", color: "#dc2626" } }
const stepsConfig: ChartConfig = { steps: { label: "Steps", color: "#2563eb" } }

function rangeButtonCls(active: boolean) {
    return active
        ? 'px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg'
        : 'px-4 py-2 text-sm font-medium bg-gray-200 rounded-lg hover:bg-gray-300'
}

// Current-state pill colors keyed by Health Services activity state.
function statePillCls(state: string) {
    switch (state.toUpperCase()) {
        case 'ASLEEP': return 'bg-indigo-100 text-indigo-700'
        case 'EXERCISE': return 'bg-green-100 text-green-700'
        case 'PASSIVE': return 'bg-gray-100 text-gray-700'
        default: return 'bg-amber-100 text-amber-700'   // UNKNOWN + anything else
    }
}

export function WatchHealthReport(props: WatchHealthReportProps) {
    const [hr, setHr] = useState<{ samples: WatchHrSample[]; bucketed: boolean } | null>(null)
    const [daily, setDaily] = useState<WatchDay[] | null>(null)
    const [states, setStates] = useState<WatchActivityState[] | null>(null)
    const [days, setDays] = useState<Range>(30)
    // Per-feed: true when the selected range was empty and we widened to full
    // history. Tracked separately because the watch can keep sending heart rate
    // after the daily activity feed goes quiet (or vice versa).
    const [hrFellBack, setHrFellBack] = useState(false)
    const [dailyFellBack, setDailyFellBack] = useState(false)

    useEffect(() => {
        let cancelled = false
        const uid = props.beneficiaryUserId
        if (uid == null) {
            setHr({ samples: [], bucketed: false }); setDaily([]); setStates([])
            setHrFellBack(false); setDailyFellBack(false)
            return
        }
        // Don't clear existing data on a range switch — keeps the panel from
        // flickering out and back in while the new window loads.
        const loadHr = async () => {
            let r = await DataService.getWatchHeartRate(uid, days)
            let widened = false
            if (r.samples.length === 0) {
                r = await DataService.getWatchHeartRate(uid, ALL_HISTORY_DAYS)
                widened = r.samples.length > 0
            }
            if (cancelled) return
            setHr(r)
            setHrFellBack(widened)
        }
        loadHr().catch(() => { if (!cancelled) { setHr({ samples: [], bucketed: false }); setHrFellBack(false) } })

        const loadDaily = async () => {
            let rows = await DataService.getWatchDaily(uid, days)
            let widened = false
            if (rows.length === 0) {
                rows = await DataService.getWatchDaily(uid, ALL_HISTORY_DAYS)
                widened = rows.length > 0
            }
            if (cancelled) return
            setDaily(rows)
            setDailyFellBack(widened)
        }
        loadDaily().catch(() => { if (!cancelled) { setDaily([]); setDailyFellBack(false) } })

        // Activity state is deliberately NOT widened — a months-old state would
        // render as a "currently ASLEEP" pill. It stays a recent-only feed: a
        // fixed short window regardless of the chart range.
        DataService.getWatchActivityState(uid, 7)
            .then(r => { if (!cancelled) setStates(r) })
            .catch(() => { if (!cancelled) setStates([]) })
        return () => { cancelled = true }
    }, [props.beneficiaryUserId, days, props.refreshKey])

    const samples = hr?.samples ?? []
    const bucketed = hr?.bucketed === true

    // Chronological (ascending) HR series; samples come back newest-first.
    const hrData = useMemo(() => {
        return [...samples]
            .sort((a, b) => a.t - b.t)
            .map(s => {
                const dt = DateTime.fromMillis(s.t).setZone(props.timezone)
                return {
                    label: bucketed ? dt.toFormat("MMM d HH:00") : dt.toFormat("MMM d, HH:mm"),
                    fullTime: dt.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS),
                    bpm: s.bpm,
                    onDemand: s.on_demand === true,
                }
            })
    }, [samples, bucketed, props.timezone])

    // Newest HR sample by max(t) — don't trust array order, so a corrupt-clock
    // sample can't masquerade as "latest".
    const latestHr = useMemo(() => {
        if (!samples.length) return null
        return samples.reduce((best, s) => (s.t > best.t ? s : best))
    }, [samples])

    // Latest day with any activity data (newest by `day` string — ISO dates
    // sort lexically). Drives the stat tiles.
    const latestDay = useMemo(() => {
        const present = (daily || []).filter(d => d.day)
        if (!present.length) return null
        return present.reduce((best, d) => (d.day > best.day ? d : best))
    }, [daily])

    // Steps bar series, ascending by day.
    const stepsData = useMemo(() => {
        return [...(daily || [])]
            .filter(d => d.day && d.steps != null)
            .sort((a, b) => (a.day < b.day ? -1 : 1))
            .map(d => ({
                label: DateTime.fromISO(d.day).setZone(props.timezone).toFormat("MMM d"),
                steps: d.steps,
            }))
    }, [daily, props.timezone])

    // Most recent activity state by max(t), plus a few recent transitions.
    const recentStates = useMemo(() => [...(states || [])].sort((a, b) => b.t - a.t), [states])
    const currentState = recentStates[0] ?? null

    // Wait for all three feeds before deciding visibility (avoids a half-load
    // flash of an empty card).
    if (hr === null || daily === null || states === null) return null

    const hasHr = samples.length > 0
    const hasDaily = (daily.length > 0) && latestDay != null
    const hasStates = recentStates.length > 0

    // Self-hide: nothing from the watch at all → render nothing.
    if (!hasHr && !hasDaily && !hasStates) return null

    return (
        <Card className="p-4 space-y-4 shadow-md rounded-lg border" id="watch_health_report">
            <div className="flex justify-between items-center border-b pb-2">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <Activity className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32} /> Watch Health
                </h3>
            </div>

            <div className="flex justify-center gap-2">
                <button className={rangeButtonCls(days === 7)} onClick={() => setDays(7)}>7d</button>
                <button className={rangeButtonCls(days === 30)} onClick={() => setDays(30)}>30d</button>
                <button className={rangeButtonCls(days === 90)} onClick={() => setDays(90)}>90d</button>
            </div>

            {/* ── Heart Rate ─────────────────────────────────────────────── */}
            {hasHr && (
                <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                        <HeartPulse className="mr-2 text-gray-600" size={18} /> Heart Rate
                        {bucketed && <span className="ml-2 text-xs font-normal text-gray-500">(hourly avg)</span>}
                    </h4>

                    {hrFellBack && (
                        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                            No heart rate in the last {days} days — showing the full history instead.
                        </p>
                    )}

                    {latestHr && (
                        <div className="flex items-center bg-gray-100 p-2 rounded-lg space-x-3">
                            <div className="bg-gray-100 p-2 rounded-lg">
                                <HeartPulse className="text-gray-800" />
                            </div>
                            <div className="flex flex-col">
                                <p className="text-sm font-medium text-gray-800">{latestHr.bpm} bpm</p>
                                {/* Relative age matters now that the section will happily
                                    show a months-old sample rather than hide itself. */}
                                <span className="text-xs text-gray-500">
                                    Last measured · {DateTime.fromMillis(latestHr.t).setZone(props.timezone).toLocaleString(DateTime.DATETIME_MED)}
                                    {' · '}{DateTime.fromMillis(latestHr.t).setZone(props.timezone).toRelative()}
                                </span>
                            </div>
                        </div>
                    )}

                    <CardContent className="p-0">
                        <ChartContainer config={hrConfig}>
                            <LineChart accessibilityLayer data={hrData} margin={{ top: 0, left: 0, right: 0, bottom: 0 }}>
                                <CartesianGrid vertical={false} />
                                <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} angle={-60} />
                                <YAxis tickLine={false} axisLine={false} tickMargin={10} domain={['dataMin - 5', 'dataMax + 5']} />
                                <ChartTooltip
                                    cursor={false}
                                    content={
                                        <ChartTooltipContent
                                            indicator="line"
                                            labelFormatter={(_label, payload) =>
                                                (payload && (payload[0] as any)?.payload?.fullTime) || ''
                                            }
                                        />
                                    }
                                />
                                <Line
                                    dataKey="bpm" type="monotone" stroke="#dc2626" strokeWidth={2}
                                    connectNulls={true} isAnimationActive={false}
                                    // Enlarge on-demand spot readings so they stand out from passive samples.
                                    dot={(dotProps: any) => {
                                        const { cx, cy, key, payload } = dotProps
                                        const r = payload?.onDemand ? 4 : 2
                                        return <circle key={key} cx={cx} cy={cy} r={r} fill="#dc2626" />
                                    }}
                                />
                            </LineChart>
                        </ChartContainer>
                    </CardContent>
                </div>
            )}

            {/* ── Daily Activity ─────────────────────────────────────────── */}
            {hasDaily && latestDay && (
                <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                        <Footprints className="mr-2 text-gray-600" size={18} /> Daily Activity
                        {/* The tiles below are a single day's totals, so name that day —
                            without it a months-old row reads as "today". */}
                        <span className="ml-2 text-xs font-normal text-gray-500">
                            {DateTime.fromISO(latestDay.day).setZone(props.timezone).toFormat("MMM d, yyyy")}
                            {' · '}{DateTime.fromISO(latestDay.day).setZone(props.timezone).toRelative()}
                        </span>
                    </h4>

                    {dailyFellBack && (
                        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                            No activity in the last {days} days — showing the full history instead.
                        </p>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        {latestDay.steps != null && (
                            <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                <div className="flex justify-between items-center">
                                    <p className="text-sm font-medium text-gray-800">{latestDay.steps.toLocaleString()}</p>
                                    <Footprints className="text-gray-800" />
                                </div>
                                <span className="text-xs text-gray-500">Steps</span>
                            </div>
                        )}
                        {latestDay.calories != null && (
                            <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                <div className="flex justify-between items-center">
                                    <p className="text-sm font-medium text-gray-800">{Math.round(latestDay.calories).toLocaleString()} kcal</p>
                                    <Flame className="text-gray-800" />
                                </div>
                                <span className="text-xs text-gray-500">Calories</span>
                            </div>
                        )}
                        {latestDay.distance != null && (
                            <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                <div className="flex justify-between items-center">
                                    <p className="text-sm font-medium text-gray-800">{(latestDay.distance / 1000).toFixed(2)} km</p>
                                    <Route className="text-gray-800" />
                                </div>
                                <span className="text-xs text-gray-500">Distance</span>
                            </div>
                        )}
                        {latestDay.floors != null && (
                            <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                <div className="flex justify-between items-center">
                                    <p className="text-sm font-medium text-gray-800">{latestDay.floors}</p>
                                    <Building className="text-gray-800" />
                                </div>
                                <span className="text-xs text-gray-500">Floors</span>
                            </div>
                        )}
                    </div>

                    {stepsData.length > 0 && (
                        <CardContent className="p-0">
                            <ChartContainer config={stepsConfig}>
                                <BarChart accessibilityLayer data={stepsData} margin={{ top: 0, left: 0, right: 0, bottom: 0 }}>
                                    <CartesianGrid vertical={false} />
                                    <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} angle={-60} />
                                    <YAxis tickLine={false} axisLine={false} tickMargin={10} />
                                    <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" />} />
                                    <Bar dataKey="steps" fill="#2563eb" radius={2} isAnimationActive={false} />
                                </BarChart>
                            </ChartContainer>
                        </CardContent>
                    )}
                </div>
            )}

            {/* ── Activity State ─────────────────────────────────────────── */}
            {hasStates && currentState && (
                <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                        <Clock className="mr-2 text-gray-600" size={18} /> Activity State
                    </h4>

                    <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statePillCls(currentState.state)}`}>
                            {currentState.state}
                        </span>
                        <span className="text-xs text-gray-500">
                            {DateTime.fromMillis(currentState.t).setZone(props.timezone).toRelative()}
                        </span>
                    </div>

                    {recentStates.length > 1 && (
                        <ul className="space-y-1">
                            {recentStates.slice(1, 6).map((s, i) => (
                                <li key={`${s.t}-${i}`} className="flex justify-between items-center text-sm text-gray-700 bg-gray-50 px-3 py-1 rounded">
                                    <span>{s.state}</span>
                                    <span className="text-xs text-gray-500">
                                        {DateTime.fromMillis(s.t).setZone(props.timezone).toRelative()}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </Card>
    )
}
